import { call, getUsuarioId, listarPrestadorOpts } from '../fundo_liquido/fundosService';
import { Opt } from '../fundo_liquido/mock';
import { AtivoForm } from './types';
import { getTipoDef, CampoDef, SecaoDef, campoVisivel } from './camposConfig';

const nz = (s?: string): string | undefined => (s && String(s).trim() ? String(s).trim() : undefined);
const dataIso = (s?: string): string | undefined => {
  const v = (s || '').trim();
  if (!v) return undefined;
  const br = v.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  return br ? `${br[3]}-${br[2]}-${br[1]}` : v;
};
const digitos = (s?: string): string | undefined => {
  const d = (s || '').replace(/\D/g, '');
  return d || undefined;
};
const moeda = (s?: string, casas = 2): string | undefined => {
  if (!s || !String(s).trim()) return undefined;
  const n = Number(String(s).replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) ? n.toFixed(casas) : undefined;
};
const fracao = (s?: string, casas = 6): string | undefined => {
  if (!s || !String(s).trim()) return undefined;
  const n = Number(String(s).replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) ? (n / 100).toFixed(casas) : undefined;
};
const toNum = (x: unknown): number | undefined => {
  if (x === null || x === undefined || x === '') return undefined;
  const n = typeof x === 'number' ? x : Number(x);
  return Number.isFinite(n) ? n : undefined;
};

function serializarCampo(c: CampoDef, v: any): any {
  switch (c.tipo) {
    case 'percent': return fracao(v, c.escala ?? 6);
    case 'money': return moeda(v, c.escala ?? 2);
    case 'number': return toNum(v);
    case 'date': return dataIso(v);
    case 'checkbox': return !!v;
    case 'cnpj': return digitos(v);
    case 'entidade': return v ? Number(v) : undefined;
    default: return nz(v);
  }
}

function serializarSecao(secao: SecaoDef, dados: Record<string, any>): Record<string, any> {
  const out: Record<string, any> = {};
  for (const c of secao.campos) {
    if (!campoVisivel(c, dados)) continue;
    out[c.nome] = serializarCampo(c, dados[c.nome]);
  }
  return out;
}

function mapAtivo(form: AtivoForm) {
  const def = getTipoDef(form.tipo_ativo);
  const secoes = def?.secoes ?? [];
  const renda_fixa: Record<string, any> = {};
  const detalhe: Record<string, any> = {};
  for (const s of secoes) {
    const bloco = serializarSecao(s, form.dados);
    Object.assign(s.destino === 'renda_fixa' ? renda_fixa : detalhe, bloco);
  }
  return {
    codigo_interno: nz(form.codigo_interno),
    nome_curto: nz(form.nome_curto),
    emissor_id: form.emissor_id ? Number(form.emissor_id) : undefined,
    tipo_ativo: form.tipo_ativo,
    is_active: !!form.is_active,
    observacao: nz(form.observacao),
    renda_fixa: def?.familiaRF ? renda_fixa : undefined,
    detalhe,
    fundos: (form.fundos || [])
      .filter((f) => f.fundo_id)
      .map((f) => ({
        id: f.id,
        fundo_id: Number(f.fundo_id),
        data_inicio: dataIso(f.data_inicio),
        data_fim: dataIso(f.data_fim),
        observacao: nz(f.observacao),
      })),
  };
}

export interface CadastroAtivoResult { ativoId: number; }

export async function cadastrarAtivo(form: AtivoForm): Promise<CadastroAtivoResult> {
  const usuario_id = getUsuarioId();
  const resp = await call('POST', '/ativos', { usuario_id, ...mapAtivo(form) });
  return { ativoId: resp?.ativo_id };
}

const str = (x: unknown): string | undefined => {
  const s = x === null || x === undefined ? '' : String(x).trim();
  return s ? s : undefined;
};

export async function listarEmissores(q?: string): Promise<{ id: number; cnpj: string; razao_social: string }[]> {
  const qs = q && q.trim() ? `?q=${encodeURIComponent(q.trim())}` : '';
  const data: any[] = (await call('GET', `/emissores${qs}`)) ?? [];
  return data.map((r) => ({ id: r.id, cnpj: str(r.cnpj) ?? '', razao_social: str(r.razao_social) ?? `#${r.id}` }));
}

export async function cadastrarEmissor(cnpj: string, razao_social: string): Promise<{ id: number; cnpj: string; razao_social: string }> {
  const usuario_id = getUsuarioId();
  const resp = await call('POST', '/emissores', {
    usuario_id,
    cnpj: digitos(cnpj),
    razao_social: razao_social.trim(),
  });
  return { id: resp?.id ?? resp?.emissor_id, cnpj: digitos(cnpj) ?? '', razao_social: razao_social.trim() };
}

export interface AtivoListItem {
  id: number;
  codigo_interno: string;
  nome_curto: string;
  tipo_ativo: string;
  tipo_label: string;   // subgrupo (ex.: CRI/CRA, Debênture, Ação)
  grupo_label: string;  // grupo (ex.: Renda Fixa, Renda Variável, Derivativo)
  emissor_nome: string;
  status: string;
}

export async function listarAtivos(q?: string): Promise<AtivoListItem[]> {
  const qs = q && q.trim() ? `?q=${encodeURIComponent(q.trim())}` : '';
  const data: any[] = (await call('GET', `/ativos${qs}`)) ?? [];
  return data.map((r) => ({
    id: r.id,
    codigo_interno: str(r.codigo_interno) ?? '',
    nome_curto: str(r.nome_curto) ?? '',
    tipo_ativo: r.tipo_ativo ?? '',
    tipo_label: getTipoDef(r.tipo_ativo)?.label ?? str(r.tipo_ativo) ?? '—',
    grupo_label: getTipoDef(r.tipo_ativo)?.grupoLabel ?? '—',
    emissor_nome: str(r.emissor_nome) ?? str(r.emissor_razao_social) ?? '—',
    status: r.is_active ? 'Ativo' : 'Inativo',
  }));
}

// Vínculo de carteira: em quais fundos o ativo está (vem do detalhe GET /ativos?id=).
export interface FundoDoAtivo {
  id: number;
  fundo_id: number;
  fundo_nome: string;
  data_inicio?: string;
  data_fim?: string;
  observacao?: string;
}

export async function listarFundosDoAtivo(ativoId: number): Promise<FundoDoAtivo[]> {
  const d: any = await call('GET', `/ativos?id=${ativoId}`);
  const fundos: any[] = d?.fundos ?? [];
  return fundos.map((f) => ({
    id: f.id,
    fundo_id: f.fundo_id,
    fundo_nome: str(f.fundo_nome) ?? `#${f.fundo_id}`,
    data_inicio: str(f.data_inicio),
    data_fim: str(f.data_fim),
    observacao: str(f.observacao),
  }));
}

export async function listarFundoOpts(): Promise<Opt[]> {
  const data: any[] = (await call('GET', '/fundos')) ?? [];
  return data
    .map((r) => ({ value: String(r.id), label: str(r.razao_social) ?? str(r.nome_fantasia) ?? `#${r.id}` }))
    .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
}

export { listarPrestadorOpts };
